<template>
  <div class="vy-table-wrapper">
    <div v-if="showEdit" class="column-select" ref="edit">
      <span class="modal-opener" ref="modalToggle" @click="showEditColumnsModal" @keyup.enter="showEditColumnsModal" tabindex="0">
        <i class="fas fa-pen"></i>
      </span>
    </div>
    <selectionModal 
        v-if="showEdit"
        :showModal="showModal"
        modalTitle="Select Columns"
        :options="modalOptions"
        :initialSelectedOptions="selectedColumns"
        :categories="categories"
        :sources="sources"
        @option-selection-cancel="showModal = false"
        @option-selection="handleOptionSelection"
    />
    <div class="vy-table" ref="table">
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import Tabulator from 'tabulator-tables';
import selectionModal from '../../selectionModal/src/selectionModal.vue';
import moment from 'moment-timezone'; // used for date-time sort
if (typeof window.moment==='undefined') {
    // Tabulator expects moment to be defined globally
    window.moment = moment;
  }

  let table = {
    name: 'VyTable',
    props: {
      columns: { type: Array, default: () => [] },
      categories: { type: Array, default: () => [] },
      sources: { type: Array, default: () => [] },
      shownColumns: { type: Array, default: () => [] },
      childColumns: { type: Array, default: () => [] },
      childAutoFetchConfig: { type: Object, default: () => {} },
      dataIndex: { type: String, default: undefined },
      disableMultiColumnSort: { type: Boolean, default: false },
      selectable: { type: Number, default: 1 }, // -1 for unlimited, 0 for none, greater allows up to that number
      selectedRows: { type: Array, default: () => [] },
      showRowSelectionColumn: { type: Boolean, default: false },
      showEdit: { type: Boolean, default: false },
      emitEdit: { type: Boolean, default: false },
      tableData: { type: Array, default: () => [] },
      filter: { type: [Array, Function], default: () => [] },
      autoFetchConfig: { type: Object, default: () => {} },
      reloadData: { type: Number, default: () => 1 },
      setFirstPage: { type: Number, default: () => 1 },
      pageSize: { type: Number, default: () => 25 },
      persistenceID: { type: String, default: undefined },
      nestedFieldSeparator: { type: String, default: '|' },
      placeholder: { type: String, default: () => 'No data available' },
      disableRowSelection: { type: Boolean, default: false } // Used to dynamically, temporarily disable row selection (e.g. for the dataSelectionTable component)
    },
    components: {
      selectionModal
    },
    data: function () {
      return {
        tabulator: null,
        showModal: false,
        selectedColumns: this.shownColumns.length ? this.shownColumns : this.columns.filter(col => col.hasOwnProperty('visible') ? col.visible : true).map(col => col.field), // default to showing all columns unless prefs are passed
        prevSelectedRows: [], // this helps track row selections across pages
        currentPageSelectedRows: [], // this tracks selections only on the current page, and adds them to prevSelectedRows once the page is changed
        pauseSelectionUpdates: false
      }
    },
    computed: {
      visibleColumns: function() {
        // columns are visible by default
        return this.columns.filter(col => col.hasOwnProperty('visible') ? col.visible : true);
      },
      modalOptions: function() {
        return this.visibleColumns.filter(c => c.field).map(col => {
          return {
            name: col.field,
            title: col.title,
            description: col.description
          }
        });
      }
    },
    methods: {
      handleOptionSelection: function(selection) {
        this.showModal = false;
        if (selection && selection.length) {
          this.selectedColumns = [...selection];
          this.emitColumnSelection()
          this.setupColumns();
        }
      },
      setupColumns: function() {
        let self = this;
        if (self.tabulator) {
          self.visibleColumns.forEach(col => {
            if (col.hasOwnProperty('field')) {
              if (!self.selectedColumns.includes(col.field)) {
                self.tabulator.hideColumn(col.field);
              } else {
                self.tabulator.showColumn(col.field);
              }
            }
          });
          self.tabulator.redraw();
        }
      },
      emitColumnSelection: function() {
        /**
         * Column selection changed (emitted only when the modal is closed)
         * 
         * @type {Array}
         */
        this.$emit('column-selection', this.selectedColumns);
      },
      emitRowClick: function(event, row) {
        /**
         * Row clicked
         * 
         * @type {Object} the event object
         * @type {RowComponent} the tabulator row component of the clicked row
         */
        this.$emit('row-click', event, row);
      },
      emitRowExpanded: function(row, subTable) {
        /**
         * Row with child data clicked
         * 
         * @type {RowComponent} the tabulator row component of the clicked row
         * @type {Tabulator} the tabulator table that will hold the data
         */
        this.$emit('row-expanded', row, subTable);
      },
      normalizeSelectedRows: function() {
        let self = this;
        if (self.showRowSelectionColumn) {
          // This function assumes that the prevSelectedRows array holds ALL selected rows, and attempts to split the data
          // into prevSelectedRows and currentPageSelectedRows, selecting rows in the table appropriately along the way.
          self.pauseSelectionUpdates = true;
          self.currentPageSelectedRows = [];

          let allCurrentPageRows = self.tabulator.getData();

          // This loop selects rows on the current page that need to be selected and adds them to currentPageSelectedRows
          for (let i = 0, _i = self.prevSelectedRows.length; i < _i; i++) {
            let dataIndexValue = self.prevSelectedRows[i][self.dataIndex];
            if (self.tabulator.getRow(dataIndexValue)) {
              self.tabulator.selectRow(dataIndexValue);
              self.currentPageSelectedRows.push(self.prevSelectedRows[i]);
            }
          }

          // This loop checks all the rows on the current page and deselects the ones that don't also exist in currentPageSelectedRows
          for (let j = 0, _j = allCurrentPageRows.length; j < _j; j++) {
            // make sure dataIndex values are interpreted as strings instead of ints
            let dataIndexValue = '' + allCurrentPageRows[j][self.dataIndex];
            let isRowSelected = !!self.currentPageSelectedRows.find(selectedRow => '' + selectedRow[self.dataIndex] === dataIndexValue);

            if (!isRowSelected) {
              self.tabulator.deselectRow(dataIndexValue);
            }
          }

          self.prevSelectedRows = _.differenceWith(self.prevSelectedRows, self.currentPageSelectedRows, _.isEqual);
          self.pauseSelectionUpdates = false;
        }
      },
      showEditColumnsModal: function () {
        let self = this;
        if (self.emitEdit) {
          self.$emit('edit-columns');
        } else {
          self.showModal = true;
        }
      }, getTabulatorColumnsAndInitialSort: function(columns) {
        let self = this;

        var headerMenu = self.showEdit ? [
          {
            label:"<i class='fal fa-pen'></i> Edit Columns...",
            action:function(){
              self.showEditColumnsModal();
            }
          },
        ] : null;

        let initialSort = [];
        let tabulatorCols = columns.map(col => {
          // We need to remove some properties from the columns array that will be used for tabulator
          // because any unexpected column properties will trigger a console.warn from tabulator
          let newCol = {
            minWidth: col.minWidth,
            headerContextMenu: headerMenu,
          };
          if (col.hasOwnProperty('title')) {
            newCol.title = col.title;
          } else {
            newCol.title = '';
          }
          if (col.hasOwnProperty('field')) {
            newCol.field = col.field;
          }
          if (col.hasOwnProperty('formatter')) {
            newCol.formatter = col.formatter;
          }
          if (col.hasOwnProperty('hozAlign')) {
            newCol.hozAlign = col.hozAlign;
          }
          if (col.hasOwnProperty('width')) {
            newCol.width = col.width;
          }
          if (col.hasOwnProperty('maxWidth')) {
            newCol.maxWidth = col.maxWidth;
          }
          if (col.hasOwnProperty('widthGrow')) {
            newCol.widthGrow = col.widthGrow;
          }
          if (col.hasOwnProperty('widthShrink')) {
            newCol.widthShrink = col.widthShrink;
          }
          if (col.hasOwnProperty('resizable')) {
            newCol.resizable = col.resizable;
          } else {
            newCol.resizable = 'header';
          }
          if (col.hasOwnProperty('frozen')) {
            newCol.frozen = col.frozen;
          }

          if (col.hasOwnProperty('cellClick')) {
            newCol.cellClick = col.cellClick;
          }
          if (col.hasOwnProperty('contextMenu')) {
            newCol.contextMenu = col.contextMenu;
          }
          if (col.hasOwnProperty('align')) {
            newCol.align = col.align;
          }
          if (col.hasOwnProperty('headerSort')) {
            newCol.headerSort = col.headerSort;
          }
          if (col.hasOwnProperty('sort')) {
            initialSort.push({
              column: col.field,
              dir: col.sort
            });
          }
          if (col.hasOwnProperty('sorter')) {
            newCol.sorter = col.sorter;
          }
          if (col.hasOwnProperty('headerSortStartingDir')) {
            newCol.headerSortStartingDir = col.headerSortStartingDir;
          }
          return newCol;
        });
        return { columns: tabulatorCols, initialSort: initialSort };
      }
    },
    watch:{
      visibleColumns: {
        deep: false,
        handler (newColumns, oldColumns) {
          if (!_.isEqual(JSON.parse(JSON.stringify(newColumns)), JSON.parse(JSON.stringify(oldColumns)))) {
            let self = this;
            let sortList = self.tabulator.getSorters().map(c =>  { return { column: c.field, dir: c.dir}; });

            // todo refactor
            let tableColsAndInitialSort = self.getTabulatorColumnsAndInitialSort(newColumns);
            let tableCols = tableColsAndInitialSort.columns;
            if (self.showRowSelectionColumn) {
              // Add the checkbox column  
              tableCols.unshift({
                formatter: function(cell, formatterParams){
                  let checkbox = document.createElement("input");

                  checkbox.type = 'checkbox';

                  if(this.table.modExists("selectRow", true)){

                    checkbox.addEventListener("click", (e) => {
                      e.stopPropagation();
                    });

                    if(typeof cell.getRow == 'function'){
                      var row = cell.getRow();

                      // if(row instanceof RowComponent){
                      if(row._row){

                        checkbox.addEventListener("change", (e) => {
                          row.toggleSelect();
                          // fix for checkbox being selected when row is not selectable
                          let checked = row.isSelected && row.isSelected();
                          let cb = e.target;
                          cb.checked = checked;
                        });

                        checkbox.checked = row.isSelected && row.isSelected();
                        this.table.modules.selectRow.registerRowSelectCheckbox(row, checkbox);
                      }else{
                        checkbox = "";
                      }
                    }else {
                      checkbox.addEventListener("change", () => {
                        if(this.table.modules.selectRow.selectedRows.length){
                          this.table.deselectRow();
                        }else {
                          this.table.selectRow(formatterParams.rowRange);
                        }
                      });

                      this.table.modules.selectRow.registerHeaderSelectCheckbox(checkbox);
                    }
                  }
                  return checkbox;
                },
                titleFormatter: "rowSelection",
                hozAlign: "center",
                headerSort: false,
                width: 40
              });
            }

            self.tabulator.setColumns(tableCols);
            self.tabulator.setSort(sortList)
            self.selectedColumns = newColumns.map(col => col.field);
          }
        }
      },
      // update table if data changes
      tableData:{
        handler: function (newData) {
          this.tabulator.replaceData(newData);
        },
        deep: true,
      },
      filter: {
        handler: function (newFilter) {
          this.tabulator.setFilter(newFilter);
        },
        deep: true,
      },
      reloadData: function () {
        if (!this.autoFetchConfig) {
          // console.warn('Attempted to reload data, but no autoFetchConfig prop detected. ' +
          //              'Reload data function is only available when the table is configured for Ajax data loading.');
        } else {
          // TODO: this assumption that only 1 row might be selected is outdated. Selection logic should happen in tabulator callbacks.
          let selectedRow = this.tabulator.getSelectedRows()[0];
          this.tabulator.replaceData().then(() => {
            if (selectedRow) {
              let rowIndex = selectedRow.getIndex();
              if (rowIndex) {
                let rowExistsInTable = !!this.tabulator.getRow(rowIndex);
                if (rowExistsInTable) {
             // we need all these checks because calling 'selectRow' when the row doesn't exist in the table will cause all rows to be selected
                  this.tabulator.selectRow(selectedRow.getIndex());
                }
              }
            }
          })
          .catch(function(error){
            // TODO: Error handling
            throw error;
          });
        }
      },
      setFirstPage: function () {
        if (this.tabulator) {
          this.tabulator.setPage('first');
        }
      },
      shownColumns: function(cols) {
        this.selectedColumns = cols.length ? cols : this.visibleColumns.filter(c => c.field).map(col => col.field);
        this.setupColumns();
      },
      selectedRows: function(rowsData) {
        let self = this;
        // rudimentary validation: we assume that rowsData with the matching dataIndex value for this table is valid, selectable data
        if (self.dataIndex && (!rowsData.length || rowsData[0].hasOwnProperty(self.dataIndex))) {
          self.prevSelectedRows = rowsData;
          self.normalizeSelectedRows();
        }
      }
    },
    mounted(){
      let self = this,
          cfg = {
            placeholder: self.placeholder,
            paginationSize: self.pageSize
          };

      if (self.tableData) {
        cfg.data = self.tableData;
      }
      if (self.filter) {
        cfg.initialFilter = self.filter;
      }
      if (self.nestedFieldSeparator && self.nestedFieldSeparator !== '.') {
        cfg.nestedFieldSeparator = self.nestedFieldSeparator;
      }
      if (self.dataIndex) {
        cfg.index = self.dataIndex;
      }
      if (self.persistenceID) {
        cfg.persistenceID = self.persistenceID;
      }
      if (self.autoFetchConfig) {
        addAjaxControlsToConfig(cfg, self.autoFetchConfig);
      }
      if (!!self.childColumns && self.childColumns.length) {
        let childColumns = self.childColumns;

        cfg.rowFormatter = function(row) {
          // adapted from http://tabulator.info/examples/4.3#nested-tables
          let rowEl = row.getElement(),
              cells = row.getCells(),
              firstCellEl = cells[0].getElement(),
              toggleBtn = document.createElement('div'),
              toggleIcon = document.createElement('i'),
              holderEl = document.createElement('div'),
              tableEl = document.createElement('div');

          holderEl.classList.add('subtable-container', 'hidden');
          tableEl.classList.add('subtable');

          holderEl.appendChild(tableEl);
          rowEl.appendChild(holderEl, rowEl);
          toggleBtn.appendChild(toggleIcon);

          firstCellEl.insertBefore(toggleBtn, firstCellEl.firstChild);
          
          toggleBtn.classList.add('toggle-btn');
          toggleIcon.classList.add('fa', 'fa-plus-square', 'row-toggle-icon');
          toggleBtn.setAttribute('tabindex', '0');

          let subtableColsAndInitialSort = self.getTabulatorColumnsAndInitialSort(childColumns);
          let subtableCols = subtableColsAndInitialSort.columns;
          let subtableInitialSort = subtableColsAndInitialSort.initialSort;

          let updateSubtablePaginationHtml = function() {
            let pageText = rowEl.querySelector('span.tabulator-pages');
            if (pageText) {
              pageText.innerHTML = `Page ${this.getPage()} of ${this.getPageMax()}`
            }
            let paginatorEls = rowEl.querySelectorAll('.tabulator-paginator');
            if (paginatorEls && paginatorEls.length) {
              paginatorEls.forEach(el => {
                el.querySelector('button[data-page="first"]').innerHTML = '<i class="fas fa-backward" tabindex="0"></i>';
                el.querySelector('button[data-page="prev"]').innerHTML = '<i class="fas fa-caret-left" tabindex="0"></i>';
                el.querySelector('button[data-page="next"]').innerHTML = '<i class="fas fa-caret-right" tabindex="0"></i>';
                el.querySelector('button[data-page="last"]').innerHTML = '<i class="fas fa-forward" tabindex="0"></i>';
              });
            }
          };
          let childCfg = {
            ajaxConfig: {
              method: 'post',
              parent: row
            },
            columnHeaderSortMulti: false,
            layout: "fitColumns",
            pagination: self.childAutoFetchConfig ? "remote" : "local",
            paginationSize: 10,
            placeholder: self.placeholder,
            headerSortTristate: true,
            columns: subtableCols,
            movableColumns: true,
            initialSort: subtableInitialSort,
            tooltipsHeader: true,
            renderComplete: updateSubtablePaginationHtml,
          };

          if (self.childAutoFetchConfig) {
            addAjaxControlsToConfig(childCfg, self.childAutoFetchConfig);
          }
          var subTable = new Tabulator(tableEl, childCfg);

          toggleBtn.addEventListener('click', () => {
            toggleListener();
          });
          toggleBtn.addEventListener('keypress', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              toggleListener();
            }
          });
          function toggleListener() {
            if (holderEl.classList.contains('hidden')) {
              updateSubtablePaginationHtml.bind(subTable)();
              holderEl.classList.remove('hidden');
              toggleIcon.classList.remove('fa-plus-square');
              toggleIcon.classList.add('fa-minus-square');
              if (self.tabulator) { self.tabulator.redraw(); }
              self.emitRowExpanded(row, subTable);
            } else {
              holderEl.classList.add('hidden');
              toggleIcon.classList.remove('fa-minus-square');
              toggleIcon.classList.add('fa-plus-square');
              if (self.tabulator) { self.tabulator.redraw(); }
            }
          }
        };
      }

      let tableColsAndInitialSort = self.getTabulatorColumnsAndInitialSort(self.visibleColumns);
      let tableCols = tableColsAndInitialSort.columns;
      if (self.showRowSelectionColumn) {
        // Add the checkbox column  
        tableCols.unshift({
          formatter: function(cell, formatterParams){
            let checkbox = document.createElement("input");

            checkbox.type = 'checkbox';

            if(this.table.modExists("selectRow", true)){

              checkbox.addEventListener("click", (e) => {
                e.stopPropagation();
              });

              if(typeof cell.getRow == 'function'){
                var row = cell.getRow();

                // if(row instanceof RowComponent){
                if(row._row){

                  checkbox.addEventListener("change", (e) => {
                    row.toggleSelect();
                    // fix for checkbox being selected when row is not selectable
                    let checked = row.isSelected && row.isSelected();
                    let cb = e.target;
                    cb.checked = checked;
                  });

                  checkbox.checked = row.isSelected && row.isSelected();
                  this.table.modules.selectRow.registerRowSelectCheckbox(row, checkbox);
                }else{
                  checkbox = "";
                }
              }else {
                checkbox.addEventListener("change", () => {
                  if(this.table.modules.selectRow.selectedRows.length){
                    this.table.deselectRow();
                  }else {
                    this.table.selectRow(formatterParams.rowRange);
                  }
                });

                this.table.modules.selectRow.registerHeaderSelectCheckbox(checkbox);
              }
            }
            return checkbox;
          },
          titleFormatter: "rowSelection",
          hozAlign: "center",
          headerSort: false,
          width: 40
        });
      }
      let tableInitialSort = tableColsAndInitialSort.initialSort;
      let selectable = 1;
      if (this.selectable === -1) {
          selectable = true;
      } else if (this.selectable === 0) {
          selectable = false;
      } else if (this.selectable > 1) {
          selectable = this.selectable;
      }

      // instantiate Tabulator when element is mounted
      self.tabulator = new Tabulator(self.$refs.table, Object.assign({}, {
        columnHeaderSortMulti: !self.disableMultiColumnSort,
        columns: tableCols,
        movableColumns: true,
        initialSort: tableInitialSort,
        layout: 'fitColumns',
        cellVertAlign: 'middle',
        headerSortTristate: true,
        tooltipsHeader: true,
        rowClick: self.emitRowClick,
        selectable: selectable,
        selectableCheck: function(row) {
          // A currently selected row should always be de-selectable
          if (row.isSelected()) {
            return true;
          }
          return !self.disableRowSelection;
        },
        persistence: {
          columns: ['width'], //persist changes to the width, visible and frozen properties
        },
        persistenceWriterFunc: function(id, type, data) {
          //id - tables persistence id
          //type - type of data being persisted ("sort", "filter", "group", "page" or "columns")
          //data - array or object of data

          localStorage.setItem(id + "-" + type, JSON.stringify(data));

          /**
           * Tabulator persistence config updated.
           * This event can be handled to save persistence data behind an API call in addition to the default localStorage write.
           * 
           * @type {string} id - the persistence id [NOTE: tabulator automatically prepends 'tabulator-' to the persistenceID config]
           * @type {string} type - the type of persistence data ("sort", "filter", "group", "page" or "columns")
           * @type {array|object} data - array or object of data
           */
          self.$emit('table-persistence-updated', id, type, data);
        },
        persistenceReaderFunc: function(id, type) {
          //id - tables persistence id
          //type - type of data being persisted ("sort", "filter", "group", "page" or "columns")
          let data = localStorage.getItem(id + "-" + type);

          return data ? JSON.parse(data) : false;
        },
        sortOrderReverse: true,
        dataSorted: function(sorters) {
           /**
           * Column sorting updated
           *
           * @type {Array}
           */
          self.$emit('table-sorted', sorters);
        },
        rowSelectionChanged: function(data) {
          let fullPageSelected = data.length === self.pageSize;
          /**
           * This event signals whether or not a full page of data is selected
           * 
           * @type {Boolean}
           */
          self.$emit('full-page-selected', fullPageSelected);

          // storing row selection like this only makes sense when the dataIndex prop exists
          if (self.dataIndex && !self.pauseSelectionUpdates) {
            self.currentPageSelectedRows = data;
            let allSelectedRows = self.prevSelectedRows.concat(self.currentPageSelectedRows);

            /**
             * Row Selection Changed
             * 
             * @type {Array}
             */
            self.$emit('row-selection-changed', allSelectedRows);
          }
        },
        // This callback is designed to preserve row selections across multiple page loads when the table component doesn't
        // have access to all the table data (i.e. in the case of ajax loading where pagination is handled on the server).
        pageLoaded: function() {
          // we cannot support row re-selection on page load without a dataIndex prop
          if (self.dataIndex) {
            self.prevSelectedRows = self.prevSelectedRows.concat(self.currentPageSelectedRows);
            self.normalizeSelectedRows();
          }
        },
        // We're assuming this is always fired before the rowSelectionChanged event when using remote data
        ajaxRequesting: function() {
          // column selection persistence only works with a dataIndex prop
          if (self.dataIndex) {
            self.pauseSelectionUpdates = true;
          }
        },
        columnMoved: function() {
          if (self.childColumns) {
            // redraw rows so that the +/- icon for expanding subtables is moved back to the first column
            self.tabulator.getRows().forEach(row => row.reformat());
          }
        },
        dataLoaded: function(data) {
          let rows = this.getRows('active');
          updatePaginationText();
          self.$emit('data-loaded', data, _.map(rows, r => r.getData()), rows);
        },
        dataChanged: function(data) {
          self.$emit('data-changed', data, _.map(this.getRows('active'), r => r.getData()));
        },
        dataFiltered:function(filters, rows){
          self.$emit('data-filtered', this.getData(), _.map(rows, r => r.getData()));
        },
        renderComplete: function() {
          updatePaginationText();
        },
        virtualDomBuffer: 5000
      }, cfg));

      updateSpacer();
      

      self.setupColumns();

      // modify pagination buttons
      let paginatorEls = self.$el.querySelectorAll('.tabulator-paginator');
      if (paginatorEls && paginatorEls.length) {
        paginatorEls.forEach(el => {
          el.querySelector('button[data-page="first"]').innerHTML = '<i class="fas fa-backward" tabindex="0"></i>';
          el.querySelector('button[data-page="prev"]').innerHTML = '<i class="fas fa-caret-left" tabindex="0"></i>';
          el.querySelector('button[data-page="next"]').innerHTML = '<i class="fas fa-caret-right" tabindex="0"></i>';
          el.querySelector('button[data-page="last"]').innerHTML = '<i class="fas fa-forward" tabindex="0"></i>';
        });
      }

      // setup mark.js highlighting

      function updatePaginationText() {
        let table = self.tabulator;
        if (table) {
          let pageText = self.$el.querySelector('div.vy-table>div.tabulator-footer span.tabulator-pages');
          if (pageText) {
            pageText.innerHTML = `Page ${table.getPage()} of ${table.getPageMax()}`;
          }
        }
      }
      function updateSpacer() {
        if (self.tabulator) {

          let tableHolder = self.$el.querySelector('.tabulator-tableHolder');
          if (tableHolder && self.showEdit) {
            tableHolder.style.display = 'flex';
            if (!self.$el.querySelector('.edit-table-spacer')) {
              let d = document.createElement('div');
              d.classList.add('edit-table-spacer');
              tableHolder.appendChild(d);
            }
          }

          let table = self.$el.querySelector('.tabulator:not(.subtable)');
          if (table) {

            if (self.showEdit) {
              let edit = self.$refs.edit;
              if (edit) {
                table.appendChild(edit);
              }
            }

            // add this always - helps with last 2 px of header when there are scroll bars and when there is edit button
            if (!self.$el.querySelector('.edit-table-spacer2')) {
              let d = document.createElement('div');
              d.classList.add('edit-table-spacer2');
              table.appendChild(d);
            }
          }
        }
      }
    }
  }
  function addAjaxControlsToConfig(config, autoFetchConfig) {
      if (autoFetchConfig.url) {
        config.ajaxURL = autoFetchConfig.url;
      }
      if (autoFetchConfig.ajaxRequestFunc) {
        config.ajaxRequestFunc = autoFetchConfig.ajaxRequestFunc;
      }
      if (autoFetchConfig.ajaxRequesting) {
        config.ajaxRequesting = autoFetchConfig.ajaxRequesting;
      }
      if (autoFetchConfig.config) {
        config.ajaxConfig = autoFetchConfig.config;
      }
      if (!autoFetchConfig.localSort) {
        config.ajaxSorting = true;
      }
      if (!autoFetchConfig.noPagination) {
        config.pagination = 'remote';
      }
  }
  export default table;
</script>

<style lang="scss">
// tabulator theme included in a <head> link

.vy-table-wrapper {
  position: relative;
  overflow-y: clip;

  .column-select {
    position: absolute;
    top: 0;
    right: 0;
  }
  
  .modal-opener {
    height: 42px;
    width: 42px;
    cursor: pointer;
    font-size: 16px;
    background-color: var(--table-header-color);
    color: var(--text-color);
    border-left: 2px solid #ffffff;
    display: flex;
    justify-content: center;
    align-items: center;
    border-top-right-radius: 24px;
    &:hover, &:focus {
      background-color: var(--table-header-hover-color);
    }

    i {
      margin-right: -1px;
    }
  }

  .vy-table {
    .subtable-container {
      background-color: var(--background-color);

      &.hidden {
        display: none;
      }

      .subtable.tabulator {
        border: none;
        border-bottom: 1px solid #BFBFBF;

        .tabulator-header {
          border-top: none;
          border-bottom: 1px solid #BFBFBF;

          .tabulator-col {
            background-color: var(--background-color);
            color: var(--text-color);
            border-right: none;

            &.tabulator-sortable:hover {
              background-color: #6D787E;
            }

            &.tabulator-sortable[aria-sort=asc] .tabulator-col-content .tabulator-arrow,
            &.tabulator-sortable[aria-sort=desc] .tabulator-col-content .tabulator-arrow {
              border-bottom-color: var(--text-color);
              border-top-color: var(--text-color);
            }
            &.tabulator-sortable:hover[aria-sort=none] .tabulator-col-content .tabulator-arrow {
              border-bottom-color: #f7f7f7;
            }

            .tabulator-col-content .tabulator-arrow {
              border-bottom-color: #FFFFFF00;
              border-top-color: #FFFFFF00;
            }


            .tabulator-col-title {
              padding-right: 8px;
              padding-left: 8px;
            }

          }
        }

        .tabulator-row.tabulator-row-even {
          background-color: var(--panel-background);
        }
      }
    }


    &.tabulator .toggle-btn {
      margin-right: 6px;
      display: inline-block;
      position: relative;
      top: -5px;

      i {
        font-size: 12px;
        position: relative;
        top: 5px;
        color: var(--default-color);
      }
    }

    // override tabulator styles
    &.tabulator, .tabulator {
      border-radius: 24px;
      border: none;
      font-size: 12px;
      .tabulator-tableHolder {
        .tabulator-table {
          color: var(--text-color);
          background-color: var(--panel-background);
        }
        .tabulator-placeholder {
          align-items: unset;
          span {
            font-size: 14px;
            color: var(--text-color);
          }
        }
      }

      .tabulator-header {
        background-color: var(--table-header-color);
        .tabulator-col {
          color: var(--text-color);
          background-color: var(--table-header-color);
          font-weight: 500;
          min-height: 40px;
          line-height: 34px;

          &.tabulator-sortable:hover {
            background-color: var(--table-header-hover-color);
          }

          &.tabulator-sortable[aria-sort=asc] .tabulator-col-content .tabulator-arrow,
          &.tabulator-sortable[aria-sort=desc] .tabulator-col-content .tabulator-arrow {
            border-bottom-color: var(--text-color);
            border-top-color: var(--text-color);
          }
          &.tabulator-sortable:hover[aria-sort=none] .tabulator-col-content .tabulator-arrow {
            border-bottom-color: #5c5c5c;
          }

          .tabulator-col-title {
            padding-right: 8px;
            padding-left: 8px;
          }


          &.tabulator-sortable .tabulator-col-content {

            .tabulator-arrow {
              top: 17px;
              border-width: 5px;
              border-bottom-color: #FFFFFF00;
              border-top-color: #FFFFFF00;
            }
          }
        }
      }

      .tabulator-row {
        border: none;
        background-color: var(--background-color);
        &.tabulator-selectable:hover {
          background-color: var(--table-header-hover-color) !important;
        }

       
        &.tabulator-selected.tabulator-row-even {
          filter: brightness(95%);
        }
        &.tabulator-selected.tabulator-row-odd {
          background-color: var(--active-background);
        }

        &.tabulator-selectable.tabulator-selected.tabulator-row-even:hover {
          background-color: var(--active-background) !important;
          // tabulator styles use !important for the hover state for some reason, so we have to use it too to override
        }
        &.tabulator-selectable.tabulator-selected.tabulator-row-odd:hover {
          background-color: var(--active-background) !important;
          // tabulator styles use !important for the hover state for some reason, so we have to use it too to override
        }
        &.tabulator-row-even:not(.tabulator-selected) {
          background-color: var(--panel-background);
        }
        .tabulator-cell {
          padding: 12px 13px!important;
          border-right: none;
        }
      }

      .tabulator-footer {
        border-top: none;
        text-align: left;
        padding: 5px 10px 0 10px;
        background-color: var(--panel-background);

        .tabulator-pages {
          font-weight: 400;
        }

        .tabulator-paginator {
          color: var(--text-color);
          .tabulator-page {
            width: 18px;
            height: 18px;
            border: none;
            
            padding: 0;

            i {
              position: relative;

              &.fa-backward,
              &.fa-forward {
                font-size: 12px;
              }

              &.fa-backward    {
                right: 0.5px;
              }
              &.fa-caret-left {
                right: -0.5px;
              }

              &.fa-caret-left,
              &.fa-caret-right {
                font-size: 14.5px;
                top: 1px;
              }

              &.fa-forward {
                left: 0.5px;
              }
              &.fa-caret-right {
                left: -0.5px;
              }
            }
          }
        }
      }
    }
    
    &.tabulator, 
    .tabulator {
      .tabulator-tableHolder {
        border-width: thin;
        border-bottom-style: solid;
        border-left-style: solid;
        border-color: #E4E6E7;
        border-right-style: solid;
        background-color: var(--background-color);
      }
      .subtable .tabulator-tableHolder { // having the extra boarder causes a +2px resize loop
        border-width: unset;
        border-bottom-style: unset;
        border-left-style: unset;
        border-color: unset;
        border-right-style: unset;
      }
    }
    
    .edit-table-spacer {
      min-width: 40px;
      width: 100%;
    }

    .edit-table-spacer:has(+ .tabulator-placeholder) {
      display: none;
    }
    
    .edit-table-spacer2 {
      width: 100%;
      border-bottom: 1px solid #999;
      position: absolute;
      top: 0;
      left: 0;
      height: 44px;
      z-index: -1;
    }
  }
}
</style>